import * as React from "react";
import {
  ArrowLeft, Activity, AlertTriangle, ChevronRight, Search,
  Loader2, TrendingUp, TrendingDown, Minus, BarChart3, Eye, HelpCircle, Info
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// === HELP TIP COMPONENT ===
const HelpTip = ({ text, children, side = "top" }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      {children || <HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" />}
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-slate-900 text-slate-100 px-3 py-2">
      {text}
    </TooltipContent>
  </Tooltip>
);

// Wrap a table header label with tooltip
const TH = ({ children, tip, className = "" }) => (
  <TableHead className={className}>
    <span className="inline-flex items-center gap-0.5">
      {children}
      {tip && <HelpTip text={tip} />}
    </span>
  </TableHead>
);

// Metric explanations
const METRIC_TIPS = {
  spend_usd: "Gasto total en USD del día. Cambios bruscos pueden indicar problemas de presupuesto o pausas inesperadas.",
  clicks: "Total de clics recibidos. Una caída puede indicar problemas de calidad de anuncio o competencia.",
  impressions: "Veces que se mostró el anuncio. Cambios drásticos sugieren cambios en presupuesto, pujas o audiencia.",
  conversions: "Acciones valiosas completadas (compras, leads, etc). La métrica más crítica para el negocio.",
  ctr: "Click-Through Rate: % de impresiones que generaron clic. Mide qué tan relevante es el anuncio.",
  conversion_rate: "% de clics que generaron conversión. Mide la efectividad del landing page y la oferta.",
  cpa: "Costo Por Adquisición: cuánto cuesta cada conversión. Un CPA alto reduce rentabilidad.",
  cpm: "Costo Por Mil impresiones. Indica qué tan caro es llegar a la audiencia objetivo."
};

const StatusBadge = ({ status }) => {
  const styles = {
    CRITICAL: "bg-red-100 text-red-700 border-red-200",
    WARNING: "bg-amber-100 text-amber-700 border-amber-200",
    NORMAL: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return <Badge className={`text-xs ${styles[status] || "bg-slate-100 text-slate-600"}`}>{status}</Badge>;
};

const DirectionIcon = ({ direction, className = "h-4 w-4" }) => {
  if (direction === "INCREASE") return <TrendingUp className={`${className} text-red-500`} />;
  if (direction === "DECREASE") return <TrendingDown className={`${className} text-blue-500`} />;
  return <Minus className={`${className} text-slate-400`} />;
};

const ZScoreBar = ({ zScore, warningT = 2, criticalT = 3 }) => {
  const abs = Math.abs(zScore);
  const maxZ = Math.max(criticalT + 1.5, abs + 0.5);
  const pct = Math.min((abs / maxZ) * 100, 100);
  const color = abs > criticalT ? "bg-red-500" : abs > warningT ? "bg-amber-500" : "bg-emerald-500";
  const warnPct = (warningT / maxZ) * 100;
  const critPct = (criticalT / maxZ) * 100;

  return (
    <div className="relative w-full h-3 bg-slate-100 rounded-full overflow-hidden">
      <div className={`absolute inset-y-0 left-0 ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      <div className="absolute inset-y-0 border-l border-amber-400 border-dashed" style={{ left: `${warnPct}%` }} />
      <div className="absolute inset-y-0 border-l border-red-400 border-dashed" style={{ left: `${critPct}%` }} />
    </div>
  );
};

// === CLIENT LIST VIEW ===
const ClientListView = ({ onSelectClient }) => {
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [detectionDate, setDetectionDate] = React.useState(null);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/anomaly-v2/clients`, { withCredentials: true });
        setClients(res.data.clients || []);
        setDetectionDate(res.data.detection_date);
        trackActivity("view_module", "anomaly_v2");
      } catch (e) {
        toast.error("Error al cargar clientes");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const filtered = clients.filter(c =>
    c.cliente.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      {/* How it works */}
      <div className="p-4 rounded-lg bg-indigo-50/60 border border-indigo-100 flex gap-3">
        <Info className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-600 space-y-1">
          <p className="font-medium text-slate-700">¿Cómo funciona?</p>
          <p>Este módulo compara el rendimiento de <strong>ayer</strong> contra el promedio histórico de los últimos <strong>60 días</strong>, segmentado por <strong>día de la semana</strong> (lunes vs lunes, martes vs martes). Usa <strong>Z-Score</strong> para medir qué tan lejos está cada métrica de lo normal, con umbrales adaptativos según la variabilidad de cada cuenta.</p>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-800 inline-flex items-center gap-1">
          Selecciona un Cliente
          <HelpTip text="Cada tarjeta muestra el resumen de anomalías detectadas ayer para ese cliente. Los clientes con anomalías críticas o warnings aparecen primero." side="bottom" />
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Datos del {detectionDate || "último análisis"} &middot; {clients.length} clientes monitoreados
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Buscar cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="anomaly-client-search"
        />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const hasIssues = c.critical > 0 || c.warning > 0;
          return (
            <Card
              key={c.cliente}
              className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                c.critical > 0 ? "border-red-200" : c.warning > 0 ? "border-amber-200" : "border-slate-200"
              }`}
              onClick={() => { trackActivity("select_client", "anomaly_v2", { cliente: c.cliente }); onSelectClient(c.cliente); }}
              data-testid={`client-card-${c.cliente}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-800">{c.cliente}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.accounts} cuentas</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
                <div className="flex gap-2">
                  {c.critical > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-xs">{c.critical} Critical</Badge>
                  )}
                  {c.warning > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">{c.warning} Warning</Badge>
                  )}
                  {!hasIssues && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Todo normal</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-slate-400 col-span-3 text-center py-8">No se encontraron clientes</p>
        )}
      </div>
    </div>
  );
};

// === CLIENT DASHBOARD VIEW ===
const ClientDashboardView = ({ cliente, onBack, onSelectAccount }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/anomaly-v2/dashboard/${encodeURIComponent(cliente)}`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error("Error al cargar dashboard");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [cliente]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { summary, by_metric, accounts, top_anomalies } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-clients">
          <ArrowLeft className="h-4 w-4 mr-1" />Clientes
        </Button>
        <div className="h-5 w-px bg-slate-200" />
        <div>
          <h2 className="text-xl font-bold text-slate-800">{cliente}</h2>
          <p className="text-xs text-slate-400">Análisis del {data.detection_date}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-slate-800">{summary.total}</p>
          <p className="text-xs text-slate-500 inline-flex items-center gap-0.5">Total Métricas<HelpTip text="Cantidad total de métricas evaluadas (cuentas x 8 métricas). Cada cuenta se analiza en spend, clicks, impressions, conversions, CTR, conversion rate, CPA y CPM." /></p>
        </CardContent></Card>
        <Card className={summary.critical > 0 ? "border-red-200 bg-red-50/30" : ""}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{summary.critical}</p>
            <p className="text-xs text-slate-500 inline-flex items-center gap-0.5">Critical<HelpTip text="Desviaciones extremas que superan el umbral crítico (Z-Score alto). Requieren investigación inmediata: posible error en campaña, pausas inesperadas o cambios drásticos." /></p>
          </CardContent>
        </Card>
        <Card className={summary.warning > 0 ? "border-amber-200 bg-amber-50/30" : ""}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{summary.warning}</p>
            <p className="text-xs text-slate-500 inline-flex items-center gap-0.5">Warning<HelpTip text="Desviaciones significativas que superan el umbral de advertencia pero no el crítico. Vale la pena revisar para descartar problemas." /></p>
          </CardContent>
        </Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-emerald-600">{summary.normal}</p>
          <p className="text-xs text-slate-500 inline-flex items-center gap-0.5">Normal<HelpTip text="Métricas dentro del rango esperado para este día de la semana, basado en los últimos 60 días de historial." /></p>
        </CardContent></Card>
      </div>

      {/* Metrics Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-500" />
            Anomalías por Métrica
            <HelpTip text="Muestra cuántas cuentas tienen anomalías en cada métrica. Las métricas con más anomalías pueden indicar un problema sistémico (ej: cambio de plataforma, error de tracking)." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {by_metric.map(m => {
              const hasIssue = m.critical > 0 || m.warning > 0;
              return (
                <div key={m.metric_name} className={`p-3 rounded-lg border ${
                  m.critical > 0 ? "bg-red-50 border-red-200" :
                  m.warning > 0 ? "bg-amber-50 border-amber-200" :
                  "bg-slate-50 border-slate-200"
                }`}>
                  <p className="text-sm font-medium text-slate-700 inline-flex items-center gap-0.5">
                    {m.metric_name}
                    {METRIC_TIPS[m.metric_name] && <HelpTip text={METRIC_TIPS[m.metric_name]} />}
                  </p>
                  <div className="flex gap-2 mt-1">
                    {m.critical > 0 && <span className="text-xs text-red-600 font-bold">{m.critical}C</span>}
                    {m.warning > 0 && <span className="text-xs text-amber-600 font-bold">{m.warning}W</span>}
                    {!hasIssue && <span className="text-xs text-emerald-600">OK</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Cuentas
            <HelpTip text="Lista de todas las cuentas publicitarias de este cliente que fueron analizadas. Las cuentas con más anomalías aparecen primero." />
          </CardTitle>
          <CardDescription>Haz clic en una cuenta para ver el detalle</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TH className="w-[35%]" tip="Nombre e ID de la cuenta publicitaria en la plataforma">Cuenta</TH>
                <TH className="w-[12%]" tip="Plataforma de publicidad: Google Ads, Facebook Ads, DV360, TikTok Ads">Plataforma</TH>
                <TH className="w-[10%] text-center" tip="Cantidad de métricas evaluadas para esta cuenta (máximo 8)">Métricas</TH>
                <TH className="w-[10%] text-center" tip="Métricas con desviación extrema que requieren acción inmediata">Critical</TH>
                <TH className="w-[10%] text-center" tip="Métricas con desviación significativa que vale la pena revisar">Warning</TH>
                <TH className="w-[13%] text-right" tip="El Z-Score más alto entre todas las métricas. Mayor Z-Score = mayor desviación respecto al comportamiento normal">Max Z-Score</TH>
                <TableHead className="w-[10%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map(acc => (
                <TableRow
                  key={acc.account_id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => onSelectAccount(acc.account_id)}
                  data-testid={`account-row-${acc.account_id}`}
                >
                  <TableCell>
                    <p className="font-medium text-sm text-slate-800">{acc.account_name}</p>
                    <p className="text-[10px] text-slate-400">{acc.account_id}</p>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{acc.platform}</Badge></TableCell>
                  <TableCell className="text-center text-sm">{acc.total_metrics}</TableCell>
                  <TableCell className="text-center">
                    {acc.critical > 0 ? <span className="text-red-600 font-bold">{acc.critical}</span> : <span className="text-slate-300">0</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {acc.warning > 0 ? <span className="text-amber-600 font-bold">{acc.warning}</span> : <span className="text-slate-300">0</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <span className={acc.max_z_score > 3 ? "text-red-600" : acc.max_z_score > 2 ? "text-amber-600" : "text-slate-600"}>
                      {acc.max_z_score}
                    </span>
                  </TableCell>
                  <TableCell><Eye className="h-4 w-4 text-slate-300" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Top Anomalies */}
      {top_anomalies.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Top Anomalías
              <HelpTip text="Las anomalías más severas ordenadas por prioridad. La prioridad combina el nivel de severidad con la magnitud de la desviación (Z-Score)." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TH tip="Cuenta publicitaria donde se detectó la anomalía">Cuenta</TH>
                  <TH tip="Métrica específica con comportamiento anómalo">Métrica</TH>
                  <TH className="text-right" tip="Valor observado ayer para esta métrica">Real</TH>
                  <TH className="text-right" tip="Valor promedio esperado para este día de la semana según los últimos 60 días">Esperado</TH>
                  <TH className="text-right" tip="Z-Score: cuántas desviaciones estándar se aleja del promedio. Positivo = por encima, Negativo = por debajo">Z-Score</TH>
                  <TH className="text-right" tip="Porcentaje de cambio respecto al valor esperado. Ej: +50% significa que el valor real fue 50% mayor al esperado">% Cambio</TH>
                  <TH tip="CRITICAL = desviación extrema, WARNING = desviación significativa. La flecha indica si subió o bajó">Status</TH>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top_anomalies.map((a, i) => (
                  <TableRow key={i} className="text-xs">
                    <TableCell className="font-medium text-slate-700">{a.account_name}</TableCell>
                    <TableCell className="font-mono">{a.metric_name}</TableCell>
                    <TableCell className="text-right font-mono">{a.actual_value?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono text-slate-500">{a.expected_value?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      <span className={a.anomaly_status === "CRITICAL" ? "text-red-600" : "text-amber-600"}>
                        {a.z_score > 0 ? "+" : ""}{a.z_score}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={a.pct_change > 0 ? "text-red-500" : "text-blue-500"}>
                        {a.pct_change > 0 ? "+" : ""}{a.pct_change}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <StatusBadge status={a.anomaly_status} />
                        <DirectionIcon direction={a.anomaly_direction} className="h-3 w-3" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// === ACCOUNT DETAIL VIEW ===
const AccountDetailView = ({ accountId, onBack }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/anomaly-v2/account/${accountId}`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error("Error al cargar detalle");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [accountId]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { account, metrics, baseline } = data;
  const criticals = metrics.filter(m => m.anomaly_status === "CRITICAL");
  const warnings = metrics.filter(m => m.anomaly_status === "WARNING");
  const normals = metrics.filter(m => m.anomaly_status === "NORMAL");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" />Dashboard
        </Button>
        <div className="h-5 w-px bg-slate-200" />
        <div>
          <h2 className="text-xl font-bold text-slate-800">{account.account_name}</h2>
          <div className="flex gap-3 text-xs text-slate-400 mt-0.5">
            <span>{account.platform}</span>
            <span>{account.cliente}</span>
            {account.pais && <span>{account.pais}</span>}
            {account.owner && <span>Owner: {account.owner}</span>}
            <span>Análisis: {account.detection_date}</span>
          </div>
        </div>
      </div>

      {/* Quick summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={criticals.length > 0 ? "border-red-200" : ""}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{criticals.length}</p>
            <p className="text-xs text-slate-500">Critical</p>
          </CardContent>
        </Card>
        <Card className={warnings.length > 0 ? "border-amber-200" : ""}>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{warnings.length}</p>
            <p className="text-xs text-slate-500">Warning</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{normals.length}</p>
            <p className="text-xs text-slate-500">Normal</p>
          </CardContent>
        </Card>
      </div>

      {/* All Metrics Detail */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Detalle por Métrica
            <HelpTip text="Cada tarjeta muestra una métrica analizada. El color indica la severidad: verde = normal, amarillo = warning, rojo = critical. La barra muestra visualmente qué tan lejos está del comportamiento normal." />
          </CardTitle>
          <CardDescription>Todas las métricas analizadas para esta cuenta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {metrics.map(m => (
            <div
              key={m.metric_name}
              className={`p-4 rounded-lg border ${
                m.anomaly_status === "CRITICAL" ? "bg-red-50 border-red-200" :
                m.anomaly_status === "WARNING" ? "bg-amber-50 border-amber-200" :
                "bg-slate-50 border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <DirectionIcon direction={m.anomaly_direction} />
                  <span className="font-semibold text-sm text-slate-800 inline-flex items-center gap-0.5">
                    {m.metric_name}
                    {METRIC_TIPS[m.metric_name] && <HelpTip text={METRIC_TIPS[m.metric_name]} />}
                  </span>
                  <StatusBadge status={m.anomaly_status} />
                </div>
                <span className="text-xs text-slate-400 inline-flex items-center gap-0.5">
                  {m.variability_segment} variability
                  <HelpTip text={`Variabilidad ${m.variability_segment}: ${
                    m.variability_segment === 'low' ? 'Esta métrica es muy estable (CV<0.3). Umbrales más estrictos: W=1.5, C=2.5' :
                    m.variability_segment === 'medium' ? 'Variabilidad moderada (CV 0.3-0.5). Umbrales estándar: W=2.0, C=3.0' :
                    m.variability_segment === 'high' ? 'Alta variabilidad (CV 0.5-0.8). Umbrales más tolerantes: W=2.5, C=3.5' :
                    'Muy alta variabilidad (CV>0.8). Umbrales muy tolerantes: W=3.0, C=4.0 para evitar falsos positivos'
                  }`} />
                </span>
              </div>
              <div className="grid grid-cols-5 gap-4 text-xs mt-2">
                <div>
                  <p className="text-slate-500 inline-flex items-center gap-0.5">Real<HelpTip text="Valor observado ayer para esta métrica" /></p>
                  <p className="font-bold text-slate-800 text-sm">{m.actual_value?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500 inline-flex items-center gap-0.5">Esperado<HelpTip text="Promedio histórico para este mismo día de la semana en los últimos 60 días" /></p>
                  <p className="font-medium text-slate-600 text-sm">{m.expected_value?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500 inline-flex items-center gap-0.5">Z-Score<HelpTip text="Cuántas desviaciones estándar se aleja del promedio. |Z|>umbral warning = anomalía. Un Z-Score de +2.5 significa que el valor está 2.5 desviaciones por encima de lo normal." /></p>
                  <p className={`font-bold text-sm ${
                    m.anomaly_status === "CRITICAL" ? "text-red-600" :
                    m.anomaly_status === "WARNING" ? "text-amber-600" : "text-slate-700"
                  }`}>{m.z_score > 0 ? "+" : ""}{m.z_score}</p>
                </div>
                <div>
                  <p className="text-slate-500 inline-flex items-center gap-0.5">% Cambio<HelpTip text="Diferencia porcentual entre el valor real y el esperado. Positivo = subió, Negativo = bajó" /></p>
                  <p className={`font-bold text-sm ${m.pct_change > 0 ? "text-red-500" : "text-blue-500"}`}>
                    {m.pct_change > 0 ? "+" : ""}{m.pct_change}%
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 inline-flex items-center gap-0.5">Obs. Baseline<HelpTip text="Cantidad de datos históricos usados para calcular el promedio. Más observaciones = baseline más confiable. Mínimo requerido: 5 por día de semana." /></p>
                  <p className="text-sm text-slate-600">{m.num_baseline_observations}</p>
                </div>
              </div>
              <div className="mt-2">
                <ZScoreBar zScore={m.z_score} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Baseline Context */}
      {baseline.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Contexto del Baseline (día {metrics[0]?.day_of_week})
              <HelpTip text="Estadísticas históricas calculadas con los últimos 60 días de datos, filtradas por el mismo día de la semana. Esto permite comparar lunes contra lunes, martes contra martes, etc." />
            </CardTitle>
            <CardDescription>Estadísticas históricas para el mismo día de la semana</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TH tip="Métrica analizada">Métrica</TH>
                  <TH className="text-right" tip="Promedio del valor de esta métrica para este día de la semana en los últimos 60 días">Media</TH>
                  <TH className="text-right" tip="Desviación estándar: mide cuánto varía normalmente esta métrica. Mayor stddev = más variabilidad">Stddev</TH>
                  <TH tip="Segmento de variabilidad basado en el Coeficiente de Variación (CV = stddev/media). Determina qué tan estrictos son los umbrales">Variabilidad</TH>
                  <TH className="text-right" tip="Umbral de Z-Score para clasificar como WARNING. Si |Z-Score| supera este valor, la métrica se marca como anómala">Warning</TH>
                  <TH className="text-right" tip="Umbral de Z-Score para clasificar como CRITICAL. Desviaciones más allá de este punto son extremas y requieren acción inmediata">Critical</TH>
                  <TH className="text-right" tip="Cantidad de observaciones (días) usadas para calcular estas estadísticas. Más observaciones = estadísticas más confiables">Obs.</TH>
                </TableRow>
              </TableHeader>
              <TableBody>
                {baseline
                  .filter(b => b.day_of_week === metrics[0]?.day_of_week)
                  .map(b => (
                  <TableRow key={b.metric_name} className="text-xs">
                    <TableCell className="font-medium">{b.metric_name}</TableCell>
                    <TableCell className="text-right font-mono">{b.mean_value?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{b.stddev_value?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{b.variability_segment}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-amber-600">{b.warning_threshold}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{b.critical_threshold}</TableCell>
                    <TableCell className="text-right">{b.num_observations}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// === MAIN MODULE VIEW ===
export const AnomalyDetectionV2View = ({ onBack }) => {
  const [view, setView] = React.useState("clients"); // clients | dashboard | detail
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState(null);

  const handleSelectClient = (cliente) => {
    setSelectedClient(cliente);
    setView("dashboard");
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccount(accountId);
    setView("detail");
  };

  const handleBackToDashboard = () => {
    setSelectedAccount(null);
    setView("dashboard");
  };

  const handleBackToClients = () => {
    setSelectedClient(null);
    setView("clients");
  };

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="anomaly-v2-back">
                <ArrowLeft className="h-4 w-4 mr-2" />Volver
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-red-500">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                    Detección de Anomalías v2
                    <HelpTip text="Este módulo compara el rendimiento de ayer contra el comportamiento histórico de cada cuenta, usando Z-Score adaptativo. Detecta desviaciones significativas que requieren atención." side="bottom" />
                  </h1>
                  <p className="text-xs text-slate-500">Z-Score adaptativo por día de la semana</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {view === "clients" && (
          <ClientListView onSelectClient={handleSelectClient} />
        )}
        {view === "dashboard" && selectedClient && (
          <ClientDashboardView
            cliente={selectedClient}
            onBack={handleBackToClients}
            onSelectAccount={handleSelectAccount}
          />
        )}
        {view === "detail" && selectedAccount && (
          <AccountDetailView
            accountId={selectedAccount}
            onBack={handleBackToDashboard}
          />
        )}
      </main>
    </div>
    </TooltipProvider>
  );
};
